import { useCallback, useMemo, useState, useEffect } from "react";
import { Icon } from "@zapier/zinnia-icons";
import {
  Button,
  Link,
  ServiceIcon,
  SkeletonBlock,
  SkeletonLoader,
  SkeletonText,
  Text,
} from "@zapier/design-system";
import styles from "./AppDetailsHeader.module.css";
import {
  getAppConnectionsIndexUrl,
  getZapsByAppUrl,
  useNotification,
} from "@zapier/utils";
import {
  HydratedApp,
  useRefetchAuthentications,
  useSearchZaps,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import useCreateAppConnection from "../connections/useCreateAppConnection";
import { useConnectionCreateRules } from "../connections/useConnectionCreateRules";
import { useCanCreateManagedConnection } from "../connections/useCanCreateManagedConnection";
import {
  CREATE_BLOCK_ADMIN_LINKS,
  CREATE_BLOCK_ICONS,
  ConnectionCreateBlockedTooltip,
  useCreateBlockCopy,
} from "../connections/ConnectionCreateBlocked";
import AddConnectionModal from "../connections/AddConnectionModal";
import {
  ConnectionKebabInteractionOption,
  ManagedConnectionSource,
  useEmitConnectionKebabInteractionEvent,
  useEmitManagedConnectionCreateClickedEvent,
} from "@zapier/shared";

interface Props {
  app?: HydratedApp;
  isLoading: boolean;
  isPrivate: boolean;
  slug: string | undefined;
  selectedApi?: string;
  serviceSlug?: string;
}

export const AppDetailsHeader = ({
  app,
  isPrivate,
  isLoading,
  slug,
  selectedApi,
}: Props) => {
  const t = useTranslations("connections-header");
  const tAdd = useTranslations("add-connection-modal");
  const tRules = useTranslations("connection-create-rules");
  const [referrer, setReferrer] = useState<string>();
  const [isManagedModalOpen, setIsManagedModalOpen] = useState(false);

  const { refetchAuthentications } = useRefetchAuthentications();

  // Allow back button to go back with the same query params
  useEffect(() => {
    const originator = globalThis.document?.referrer;
    if (originator && originator.includes("/app/assets/connections?")) {
      const start = originator.indexOf("/app/assets/connections?");
      setReferrer(originator.slice(start));
    }
  }, []);

  const implementationId = app?.currentImplementationId;
  const zapsHref = getZapsByAppUrl(selectedApi || implementationId || "", slug);

  const { totalCount: totalZapsCount } = useSearchZaps({
    appId: implementationId || selectedApi,
    limit: 1,
  });

  const { emit } = useEmitConnectionKebabInteractionEvent();
  const emitAddConnectionEvent = useCallback(() => {
    emit(ConnectionKebabInteractionOption.AddConnection);
  }, [emit]);

  const canConnect = Boolean(
    app?.implementation?.authType && app?.implementation?.authType !== "None",
  );

  const serviceName = useMemo<string>(() => {
    return app?.name ?? (app as any)?.title ?? "";
  }, [app]);
  const image = app?.image ?? (app as any)?.images?.url32X32 ?? "";
  const categories = app?.categories ?? [];

  const createNewConnection = useCreateAppConnection();
  const { createNotification } = useNotification();

  const { getCreateBlock, isLoading: isCreateRulesLoading } =
    useConnectionCreateRules();
  const { canCreateManagedConnection } = useCanCreateManagedConnection();
  const { canManage } = useCreateBlockCopy();
  const { emit: emitManagedConnectionCreateClicked } =
    useEmitManagedConnectionCreateClickedEvent();
  const appId =
    app?.implementation?.selectedApi || implementationId || selectedApi;
  const createBlock = appId ? getCreateBlock(appId) : undefined;

  const openManagedConnectionModal = useCallback(() => {
    emitManagedConnectionCreateClicked(ManagedConnectionSource.AppDetails);
    setIsManagedModalOpen(true);
  }, [emitManagedConnectionCreateClicked]);

  const connectApp = useCallback(async () => {
    if (!app) {
      return;
    }
    emitAddConnectionEvent();
    const { id } = await createNewConnection(app);
    if (id) {
      // Create a notification that the connection has been added
      createNotification({
        message: tAdd("notifications.connection-added-success", {
          appName: app?.name || "",
        }),
        type: "success",
      });

      refetchAuthentications();
    }
  }, [
    app,
    createNewConnection,
    createNotification,
    refetchAuthentications,
    emitAddConnectionEvent,
    tAdd,
  ]);

  // Each case gets one control: the action the user can take, or a disabled
  // button whose tooltip explains the block.
  const renderAddConnectionAction = () => {
    if (createBlock === "managed" && canCreateManagedConnection) {
      return (
        <Button
          variant="primary"
          size="medium"
          onPress={openManagedConnectionModal}
          iconPosition="start"
        >
          <Icon name="formAdd" />
          {t("buttons.add-managed-connection")}
        </Button>
      );
    }

    if (createBlock === "restricted" && canManage) {
      return (
        <Link
          href={CREATE_BLOCK_ADMIN_LINKS.restricted}
          target="_blank"
          variant="primary"
          size="medium"
          iconPosition="end"
        >
          {tRules("buttons.manage-apps")}
          <Icon name="arrowOffsite" aria-hidden={true} />
        </Link>
      );
    }

    if (createBlock) {
      return (
        <ConnectionCreateBlockedTooltip reason={createBlock}>
          <Button
            variant="primary"
            size="medium"
            iconPosition="start"
            isDisabled={true}
          >
            <Icon name={CREATE_BLOCK_ICONS[createBlock]} aria-hidden={true} />
            {t("buttons.add-connection")}
          </Button>
        </ConnectionCreateBlockedTooltip>
      );
    }

    return (
      <Button
        variant="primary"
        size="medium"
        onPress={connectApp}
        iconPosition="start"
        isDisabled={isCreateRulesLoading}
      >
        <Icon name="formAdd" />
        {t("buttons.add-connection")}
      </Button>
    );
  };

  return (
    <div>
      <div className={styles.backLink}>
        {isLoading ? (
          <SkeletonLoader>
            <SkeletonText lines={1} size="body" width="120px" />
          </SkeletonLoader>
        ) : (
          <Link
            href={referrer || getAppConnectionsIndexUrl()}
            size="small"
            variant="secondary"
            iconPosition="start"
          >
            <Icon name="arrowSmallLeft" />
            {t("links.back")}
          </Link>
        )}
      </div>
      <div className={styles.root}>
        <div className={styles.serviceTitle}>
          {isLoading ? (
            <SkeletonLoader>
              <div className={styles.headerLoader}>
                <SkeletonBlock height={60} width={60} borderRadius="medium" />
                <div className={styles.headerLoaderText}>
                  <SkeletonText lines={1} size="heading" width="100%" />
                  <SkeletonText lines={1} size="body" width="100%" />
                </div>
              </div>
            </SkeletonLoader>
          ) : (
            <div className={styles.serviceTitleInner}>
              <div className={styles.icon}>
                {/* TODO(zinnia-10): 'large' is 48x48, previously 60x60 */}
                <ServiceIcon
                  serviceName={serviceName}
                  size="large"
                  src={image}
                />
              </div>
              <div className={styles.info}>
                <div className={styles.title}>
                  <Text tag="h1" type="PageHeader">
                    {serviceName}
                  </Text>
                  {!isPrivate && (
                    <div>
                      <Link
                        aria-label="View app info link"
                        href={`/apps/${slug}`}
                        rel="noreferrer"
                        style={{ display: "flex" }}
                        target="_blank"
                        iconPosition="start"
                      >
                        <Icon
                          name="arrowOffsite"
                          aria-label="View app info"
                          size={24}
                        />
                      </Link>
                    </div>
                  )}
                </div>
                {categories?.length > 0 && (
                  <Text color="GrayWarm9" type="Body1">
                    {categories[0].title}
                  </Text>
                )}
              </div>
            </div>
          )}
        </div>
        {canConnect && (
          <div className={`${styles.button} ${styles.buttonGroup}`}>
            <Link
              variant="tertiary"
              iconPosition="start"
              size="medium"
              href={zapsHref}
            >
              <Icon name="miscBoltAlt" />
              {t("links.view-zaps", {
                zapCount: totalZapsCount,
              })}
            </Link>
            <div className={styles.addConnectionButton}>
              {renderAddConnectionAction()}
            </div>
          </div>
        )}
      </div>
      <AddConnectionModal
        mode="managed"
        isOpen={isManagedModalOpen}
        setIsOpen={setIsManagedModalOpen}
        preselectedAppName={serviceName}
        source={ManagedConnectionSource.AppDetails}
      />
    </div>
  );
};
